import React, { useMemo, useState } from 'react';
import { Flex } from 'antd';
import { Link } from 'react-router';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import { StyledAlert } from '@app/components/alert/Alert';
import { AppConfig } from '@app/utils/app-config';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import useTokenUsage from '@app/hooks/tokenUsage/useTokenUsage';
import type { TokenUsageRow } from '@app/services/api.types';
import TokenUsageFilters from './components/TokenUsageFilters';
import ExportBtn from './components/ExportBtn';
import { TOKEN_USAGE_COLUMNS, computePct } from './utils/columns';
import { normalizeTokenUsage } from './utils/normalize';

const TokenUsageView = () => {
    const [dates, setDates] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [search, setSearch] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

    const apiParams = useMemo(
        () => ({
            start: dates.start ? fn.formatDate(dates.start, 'YYYY-MM-DD') : undefined,
            end: dates.end ? fn.formatDate(dates.end, 'YYYY-MM-DD') : undefined,
            per_page: 99999,
        }),
        [dates]
    );

    const { data, isLoading } = useTokenUsage(apiParams);

    const tree = useMemo(() => normalizeTokenUsage(Array.isArray(data) ? data : []), [data]);

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return tree;
        }
        return tree.reduce<TokenUsageRow[]>((acc, parent) => {
            if (parent.name.toLowerCase().includes(term)) {
                acc.push(parent);
                return acc;
            }
            const matchedChildren = (parent.children ?? []).filter((child) =>
                child.name.toLowerCase().includes(term)
            );
            if (matchedChildren.length) {
                acc.push({ ...parent, children: matchedChildren });
            }
            return acc;
        }, []);
    }, [tree, search]);

    const exportFileName = useMemo(() => {
        const start = dates.start ? fn.formatDate(dates.start, 'YYYY-MM-DD') : '';
        const end = dates.end ? fn.formatDate(dates.end, 'YYYY-MM-DD') : '';
        return start && end ? `${start}_to_${end}_token-usage.csv` : 'token-usage.csv';
    }, [dates]);

    const handleSearchChange = (term: string) => {
        setSearch(term);
        const t = term.trim().toLowerCase();
        if (!t) {
            setExpandedKeys([]);
            return;
        }
        const matchedBranchIds = tree
            .filter(
                (parent) =>
                    parent.name.toLowerCase().includes(t) ||
                    (parent.children ?? []).some((child) => child.name.toLowerCase().includes(t))
            )
            .map((parent) => parent.id);
        setExpandedKeys(matchedBranchIds);
    };

    return (
        <React.Fragment>
            <PageMeta title="Token Usage" />
            <StyledAlert message="BETA" />

            <PageHeader
                breadcrumbItems={[
                    { title: <Link to={url.adminPortal.dashboard}>Dashboard</Link> },
                    { title: 'Token Usage' },
                ]}>
                <PageTitle title="Token Usage" />
                <PageIntro>
                    <p>Monitor monthly interaction usage per location against the cap</p>
                </PageIntro>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <TokenUsageFilters
                            updateDates={setDates}
                            onSearchChange={handleSearchChange}
                        />
                        <div>
                            <ExportBtn rows={rows} fileName={exportFileName} />
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<TokenUsageRow>
                        className="token-usage-table"
                        columns={TOKEN_USAGE_COLUMNS}
                        dataSource={rows}
                        rowKey={({ id }) => id}
                        showSorterTooltip={false}
                        loading={isLoading}
                        pagination={{ pageSize: AppConfig.pagination_size }}
                        rowClassName={(record) => (computePct(record) > 100 ? 'is-over-cap' : '')}
                        expandable={{
                            expandedRowKeys: expandedKeys,
                            onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        }}
                        locale={{
                            emptyText: search
                                ? 'No branches or locations match your search'
                                : 'No usage data for the selected period',
                        }}
                    />
                </PageLayout.Content>
            </PageLayout>
        </React.Fragment>
    );
};

export default TokenUsageView;
